import { HttpClient } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef, computed, inject, Injectable, signal } from '@angular/core';
import { catchError, filter, finalize, firstValueFrom, fromEvent, interval, Observable, of, switchMap } from 'rxjs';

import {
	DEVICE_SESSION_POLICY_ID_STORAGE_KEY,
	DEVICE_SESSION_RULES_SNAPSHOT_STORAGE_KEY,
	DEVICE_SESSION_TOKEN_STORAGE_KEY
} from '../../constants/device-session.constants';
import { AUTH_RETURN_URL_SESSION_STORAGE_KEY } from '../../../shared/models/auth/auth.constants';
import { sanitizeReturnUrl } from '../../../shared/utils/url.util';
import { AuthService } from '../auth.service';
import {
	VERSIONING_AUTH_SIGN_IN_PATH,
	VERSIONING_EXTERNAL_AUTH_CALLBACK_PATH,
	VERSIONING_RETURN_URL_QUERY_PARAM
} from './versioning.constants';
import { DEFAULT_VERSION_CONFIG, VERSION_STORAGE_KEYS } from './versioning.config';
import { CurrentVersionState, VersionCheckConfig, VersionInfo } from './versioning.interfaces';

@Injectable({
	providedIn: 'root'
})
export class VersioningService {
	private readonly isChecking = signal<boolean>(false);
	private readonly lastChecked = signal<Date | null>(null);
	private readonly latestVersion = signal<VersionInfo | null>(null);
	private readonly currentVersion = signal<VersionInfo | null>(null);

	private readonly httpClient = inject(HttpClient);
	private readonly authService = inject(AuthService);
	private readonly destroyRef = inject(DestroyRef);

	private isInitialized = false;
	private isInitialCheckInProgress = false;
	private isInitialCheckPerformed = false;
	private isPeriodicCheckingStarted = false;
	private readonly config: VersionCheckConfig = DEFAULT_VERSION_CONFIG;

	public readonly versionState = computed<CurrentVersionState>(() => {
		const current = this.currentVersion();
		const latest = this.latestVersion();
		const isAuthenticated = this.authService.isAuthenticated();

		return {
			current,
			latest,
			updateAvailable: isAuthenticated && this.isUpdateAvailable(current, latest),
			isChecking: this.isChecking(),
			lastChecked: this.lastChecked()
		};
	});

	/**
	 * Initialize versioning service (called once from app.component)
	 *
	 * @returns {void} No value is returned by this method.
	 */
	public initialize(): void {
		if (this.isInitialized) {
			return;
		}

		this.cleanUpUrlAfterUpdate();
		this.isInitialized = true;
		this.setupAuthenticationListener();

		if (this.authService.isAuthenticated()) {
			this.onUserAuthenticated();
		}
	}

	/**
	 * Clean up URL after update if needed
	 *
	 * @returns {void} No value is returned by this method.
	 */
	private cleanUpUrlAfterUpdate(): void {
		const currentUrl = new URL(window.location.href);
		let hasUpdated = false;

		const cacheParams = [
			'_v',
			'_cache_bust',
			'_build_hash',
			'_force_reload',
			'_chunk_refresh',
			'_timestamp',
			'_random',
			'_cache_killer',
			'_fresh_load',
			'_t'
		];

		cacheParams.forEach(param => {
			if (currentUrl.searchParams.has(param)) {
				currentUrl.searchParams.delete(param);
				hasUpdated = true;
			}
		});

		if (hasUpdated) {
			window.history.replaceState({}, document.title, currentUrl.toString());
		}
	}

	/**
	 * Check for latest version from server
	 *
	 * @returns {Observable<VersionInfo | null>} An observable that emits the latest version or null if there is an error.
	 */
	public checkForUpdates(): Observable<VersionInfo | null> {
		if (!this.authService.isAuthenticated()) {
			return of(null);
		}

		this.isChecking.set(true);
		const versionUrlWithBust = `${this.config.versionUrl}?t=${Date.now()}`;
		return this.httpClient.get<VersionInfo>(versionUrlWithBust).pipe(
			catchError(() => of(null)),
			finalize(() => this.isChecking.set(false))
		);
	}

	/**
	 * Handle "Update Now" action - clear all caches and reload
	 *
	 * @param {VersionInfo} [latest] - Optional latest version to save before reload.
	 * @returns {Promise<void>} A promise that resolves when the update process is initiated.
	 */
	public async updateNow(latest?: VersionInfo): Promise<void> {
		try {
			const versionToSave = latest ?? this.latestVersion();
			if (versionToSave) {
				this.currentVersion.set(versionToSave);
				localStorage.setItem(VERSION_STORAGE_KEYS.LAST_VERSION, JSON.stringify(versionToSave));
			}

			if (this.authService.isAuthenticated()) {
				try {
					const token = await firstValueFrom(this.authService.getTemporaryToken());
					if (token) {
						const baseUrlString = this.resolveUrlForSeamlessUpdateReload();
						const currentUrl = new URL(baseUrlString);
						currentUrl.searchParams.set('t', token);
						localStorage.setItem(VERSION_STORAGE_KEYS.PENDING_AUTH_URL, currentUrl.toString());
					}
				} catch (error) {
					console.warn('Failed to get temporary token for seamless update:', error);
				}
			}

			await this.clearAllCaches();
			this.performHardReload();
		} catch (error) {
			console.error('Error during update process:', error);
			window.location.reload();
		}
	}

	/**
	 * Handle "Ask Me Later" action - delay next check
	 * @description This function is used to delay the next version check.
	 *
	 * @returns {void} No value is returned by this method.
	 */
	public askMeLater(): void {
		const timestamp = Date.now();
		localStorage.setItem(VERSION_STORAGE_KEYS.ASK_LATER_TIMESTAMP, timestamp.toString());
	}

	/**
	 * Start versioning system when user becomes authenticated
	 * Call this method after successful login
	 *
	 * @returns {Promise<void>} A promise that resolves when the initial check is complete.
	 */
	public async onUserAuthenticated(): Promise<void> {
		if (!this.authService.isAuthenticated() || this.isInitialCheckInProgress) {
			return;
		}

		this.loadCurrentVersion();

		if (!this.isInitialCheckPerformed) {
			this.isInitialCheckInProgress = true;
			try {
				const latestVersion = await firstValueFrom(this.checkForUpdates());
				if (latestVersion) {
					if (!this.currentVersion()) {
						this.currentVersion.set(latestVersion);
						localStorage.setItem(VERSION_STORAGE_KEYS.LAST_VERSION, JSON.stringify(latestVersion));
					} else if (this.isUpdateAvailable(this.currentVersion(), latestVersion)) {
						await this.updateNow(latestVersion);
						return;
					}
				}
				this.isInitialCheckPerformed = true;
			} catch (error) {
				console.warn('Initial version check failed:', error);
				this.isInitialCheckPerformed = true;
			} finally {
				this.isInitialCheckInProgress = false;
			}
		}

		this.startPeriodicChecking();
	}

	/**
	 * Stop versioning system when user logs out
	 * Call this method on logout
	 *
	 * @returns {void} No value is returned by this method.
	 */
	public onUserLoggedOut(): void {
		this.latestVersion.set(null);
		this.currentVersion.set(null);
		this.isChecking.set(false);
		this.lastChecked.set(null);
		this.isInitialCheckInProgress = false;
		this.isInitialCheckPerformed = false;
		this.isPeriodicCheckingStarted = false;
	}

	/**
	 * Setup event listener for authentication events from AuthService
	 *
	 * @returns {void} No value is returned by this method.
	 */
	private setupAuthenticationListener(): void {
		fromEvent(window, 'userAuthenticated')
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				this.onUserAuthenticated();
			});

		fromEvent(window, 'userLoggedOut')
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				this.onUserLoggedOut();
			});
	}

	/**
	 * Load current version from local storage or initialize
	 *
	 * @returns {void} No value is returned by this method.
	 */
	private loadCurrentVersion(): void {
		const stored = localStorage.getItem(VERSION_STORAGE_KEYS.LAST_VERSION);

		if (stored) {
			try {
				const version = JSON.parse(stored) as VersionInfo;
				this.currentVersion.set(version);
			} catch {
				console.warn('Failed to parse stored version');
			}
		}
	}

	/**
	 * Check if update is available by comparing build versions and respecting "Ask me later" delay
	 *
	 * @param {VersionInfo | null} current - The current version.
	 * @param {VersionInfo | null} latest - The latest version from server.
	 * @returns {boolean} True if an update is available, false otherwise.
	 */
	private isUpdateAvailable(current: VersionInfo | null, latest: VersionInfo | null): boolean {
		if (!latest || !current) return false;

		const hasNewVersion = current.buildVersion !== latest.buildVersion;

		if (!hasNewVersion) return false;

		const askLaterTimestamp = localStorage.getItem(VERSION_STORAGE_KEYS.ASK_LATER_TIMESTAMP);
		if (askLaterTimestamp) {
			const timestamp = parseInt(askLaterTimestamp, 10);
			const now = Date.now();
			const timePassed = now - timestamp;

			if (timePassed < this.config.askLaterDelay) {
				return false;
			}
			localStorage.removeItem(VERSION_STORAGE_KEYS.ASK_LATER_TIMESTAMP);
		}

		return true;
	}

	/**
	 * Start periodic checking for updates
	 *
	 * @returns {void} No value is returned by this method.
	 */
	private startPeriodicChecking(): void {
		if (this.isPeriodicCheckingStarted) {
			return;
		}

		this.isPeriodicCheckingStarted = true;

		interval(this.config.checkInterval)
			.pipe(
				filter(() => this.authService.isAuthenticated()),
				switchMap(() => this.checkForUpdates()),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe(latestVersion => {
				if (latestVersion) {
					this.latestVersion.set(latestVersion);
					this.lastChecked.set(new Date());
				}
			});
	}

	/**
	 * Clear all application caches and storage
	 */
	private async clearAllCaches(): Promise<void> {
		try {
			const lastVersion = localStorage.getItem(VERSION_STORAGE_KEYS.LAST_VERSION);
			const askLaterTimestamp = localStorage.getItem(VERSION_STORAGE_KEYS.ASK_LATER_TIMESTAMP);
			const pendingAuthUrl = localStorage.getItem(VERSION_STORAGE_KEYS.PENDING_AUTH_URL);
			const selectedRole = localStorage.getItem('selectedRole');
			const isShowSelectRoleDialog = localStorage.getItem('isShowSelectRoleDialog');
			const roleDialogShown = localStorage.getItem('roleDialogShown');
			const deviceSessionToken = localStorage.getItem(DEVICE_SESSION_TOKEN_STORAGE_KEY);
			const deviceSessionPolicyId = localStorage.getItem(DEVICE_SESSION_POLICY_ID_STORAGE_KEY);
			const deviceSessionRulesSnapshot = localStorage.getItem(DEVICE_SESSION_RULES_SNAPSHOT_STORAGE_KEY);

			localStorage.clear();
			sessionStorage.clear();

			if ('caches' in window) {
				const cacheNames = await caches.keys();

				await Promise.all(
					cacheNames.map(async cacheName => {
						try {
							await caches.delete(cacheName);
						} catch (error) {
							console.error(`Failed to delete cache ${cacheName}:`, error);
						}
					})
				);
			}

			if ('indexedDB' in window) {
				try {
					const databases = await indexedDB.databases();

					await Promise.all(
						databases.map(db => {
							if (db.name) {
								const deleteReq = indexedDB.deleteDatabase(db.name);
								return new Promise<void>((resolve, reject) => {
									deleteReq.onsuccess = () => {
										resolve();
									};
									deleteReq.onerror = () => {
										reject(deleteReq.error);
									};
									deleteReq.onblocked = () => {
										resolve();
									};
								});
							}
							return Promise.resolve();
						})
					);
				} catch (error) {
					console.warn('Failed to clear IndexedDB:', error);
				}
			}

			if ('performance' in window && 'clearResourceTimings' in window.performance) {
				window.performance.clearResourceTimings();
			}

			this.clearWebpackCache();
			this.tryGarbageCollection();

			if (lastVersion) {
				localStorage.setItem(VERSION_STORAGE_KEYS.LAST_VERSION, lastVersion);
			}
			if (askLaterTimestamp) {
				localStorage.setItem(VERSION_STORAGE_KEYS.ASK_LATER_TIMESTAMP, askLaterTimestamp);
			}
			if (pendingAuthUrl) {
				localStorage.setItem(VERSION_STORAGE_KEYS.PENDING_AUTH_URL, pendingAuthUrl);
			}
			if (selectedRole) {
				localStorage.setItem('selectedRole', selectedRole);
			}
			if (isShowSelectRoleDialog) {
				localStorage.setItem('isShowSelectRoleDialog', isShowSelectRoleDialog);
			}
			if (roleDialogShown) {
				localStorage.setItem('roleDialogShown', roleDialogShown);
			}
			if (deviceSessionToken) {
				localStorage.setItem(DEVICE_SESSION_TOKEN_STORAGE_KEY, deviceSessionToken);
			}
			if (deviceSessionPolicyId) {
				localStorage.setItem(DEVICE_SESSION_POLICY_ID_STORAGE_KEY, deviceSessionPolicyId);
			}
			if (deviceSessionRulesSnapshot) {
				localStorage.setItem(DEVICE_SESSION_RULES_SNAPSHOT_STORAGE_KEY, deviceSessionRulesSnapshot);
			}
		} catch (error) {
			console.error('Error during comprehensive cache clearing:', error);
		}
	}

	/**
	 * Perform a hard reload that bypasses ALL caches.
	 * If a pending auth URL exists (from email link), redirects to that URL
	 * so the user can be re-authenticated with the token.
	 *
	 * @returns {void} No value is returned by this method.
	 */
	private performHardReload(): void {
		const pendingAuthUrl = localStorage.getItem(VERSION_STORAGE_KEYS.PENDING_AUTH_URL);
		const targetUrl = this.buildReloadUrl(pendingAuthUrl);

		window.location.replace(targetUrl);
	}

	/**
	 * Builds the reload URL with cache-busting parameters.
	 * Uses pendingAuthUrl if available (preserves email token), otherwise uses current URL.
	 *
	 * @param {string | null} pendingAuthUrl - The pending authentication URL.
	 * @returns {string} The reload URL.
	 * @throws {Error} If the base URL is not a valid URL.
	 */
	private buildReloadUrl(pendingAuthUrl: string | null): string {
		const timestamp = Date.now();
		const baseUrl = pendingAuthUrl || window.location.origin + window.location.pathname;
		const url = new URL(baseUrl);

		url.searchParams.set('_v', timestamp.toString());
		url.searchParams.set('_cache_bust', Math.random().toString(36).substring(2, 10));

		return url.toString();
	}

	/**
	 * Resolves the base URL for storing pending post-update navigation before a hard reload.
	 * When the SPA is still on an auth route (e.g. sign-in before navigation completes), uses
	 * returnUrl from the query string or OAuth return path from sessionStorage instead of the current path.
	 *
	 * @returns {string} Absolute URL without the temporary token or cache-busting parameters.
	 */
	private resolveUrlForSeamlessUpdateReload(): string {
		const currentUrl = new URL(window.location.href);
		const pathname = currentUrl.pathname;

		if (pathname === VERSIONING_AUTH_SIGN_IN_PATH || pathname.endsWith(VERSIONING_AUTH_SIGN_IN_PATH)) {
			const fromQuery = currentUrl.searchParams.get(VERSIONING_RETURN_URL_QUERY_PARAM);
			const fromSession = sessionStorage.getItem(AUTH_RETURN_URL_SESSION_STORAGE_KEY);
			const path = sanitizeReturnUrl(fromQuery || fromSession || undefined);
			return new URL(path, window.location.origin).toString();
		}

		if (pathname.includes(VERSIONING_EXTERNAL_AUTH_CALLBACK_PATH)) {
			const fromSession = sessionStorage.getItem(AUTH_RETURN_URL_SESSION_STORAGE_KEY);
			const path = sanitizeReturnUrl(fromSession || undefined);
			return new URL(path, window.location.origin).toString();
		}

		return currentUrl.toString();
	}

	/**
	 * Attempts to clear webpack module cache if available.
	 *
	 * @returns {void} No value is returned by this method.
	 */
	private clearWebpackCache(): void {
		try {
			const webpackRequire = (window as unknown as { __webpack_require__?: { cache?: Record<string, unknown> } })
				?.__webpack_require__;
			if (webpackRequire?.cache) {
				Object.keys(webpackRequire.cache).forEach(key => {
					delete webpackRequire.cache![key];
				});
			}
		} catch (error) {
			console.warn('Failed to clear webpack cache:', error);
		}
	}

	/**
	 * Attempts to trigger garbage collection if available (Chrome DevTools).
	 *
	 * @returns {void} No value is returned by this method.
	 */
	private tryGarbageCollection(): void {
		try {
			const globalWindow = window as { gc?: () => void };
			if (globalWindow.gc) {
				globalWindow.gc();
			}
		} catch (error) {
			console.warn('Garbage collection not available:', error);
		}
	}
}
